/** Aya / www post-logout destinations by deploy tier (shared dev-auth serves both). */
export const DEV_AYA_APP_URL = 'https://id-preview--ad5b0495-bbf4-49ca-8dce-8544734ccca6.lovable.app/';
export const QA_AYA_APP_URL = 'https://qa-aya.healthexam.ai/';
export const PROD_AYA_APP_URL = 'https://aya.healthexam.ai/';
export const DEV_WWW_APP_URL = 'https://dev-www.healthexam.com/';
export const QA_WWW_APP_URL = 'https://qa-www.healthexam.ai/';
export const PROD_WWW_APP_URL = 'https://www.healthexam.com/';

export const DEV_HEALTHPAY_APP_URL = 'https://id-preview--4cbbc3f9-d56c-46ff-bdb9-a4b05c022dfa.lovable.app/';
export const QA_HEALTHPAY_APP_URL = DEV_HEALTHPAY_APP_URL;
export const PROD_HEALTHPAY_APP_URL = 'https://biller.healthexam.ai';

export const DEV_THREADS_APP_URL = 'https://id-preview--d8f36b74-92af-4ea3-986f-5982b3d1c6b8.lovable.app/';
export const QA_THREADS_APP_URL = DEV_THREADS_APP_URL;
export const PROD_THREADS_APP_URL = 'https://threads.healthexam.ai';

export const DEV_AUTOMATE_APP_URL =
  'https://id-preview--81eec655-e426-455e-a23a-0ff80984e7c4.lovable.app/';
export const QA_AUTOMATE_APP_URL = DEV_AUTOMATE_APP_URL;
export const PROD_AUTOMATE_APP_URL = 'https://automate.healthexam.ai';

export const DEV_CUSTODIAN_APP_URL =
  'https://id-preview--88d8aad4-82d0-4bc1-a2a3-9c50a3d50199.lovable.app/';
export const QA_CUSTODIAN_APP_URL = DEV_CUSTODIAN_APP_URL;
export const PROD_CUSTODIAN_APP_URL = 'https://custodian.healthexam.ai';

export type AyaDeployTier = 'dev' | 'qa' | 'prod';

/** Walk returnUrl query nesting and collect hostnames from the chain. */
export function collectHostnamesFromReturnUrl(
  raw: string | null | undefined
): string[] {
  const hosts: string[] = [];
  let current = raw?.trim();
  const seen = new Set<string>();

  while (current && !seen.has(current)) {
    seen.add(current);
    try {
      const parsed = new URL(current);
      hosts.push(parsed.hostname.toLowerCase());
      current = parsed.searchParams.get('returnUrl') ?? undefined;
    } catch {
      break;
    }
  }

  return hosts;
}

export function isQaHealthexamHost(host: string): boolean {
  const normalized = host.toLowerCase();
  return (
    normalized.startsWith('qa-') &&
    (normalized.endsWith('.healthexam.ai') ||
      normalized.endsWith('.healthexam.com'))
  );
}

export function isProdHealthexamHost(host: string): boolean {
  const normalized = host.toLowerCase();
  return (
    normalized === 'aya.healthexam.ai' ||
    normalized === 'auth.healthexam.ai' ||
    normalized === 'www.healthexam.com' ||
    normalized === 'clinic.healthexam.ai' ||
    normalized === 'biller.healthexam.ai' ||
    normalized === 'threads.healthexam.ai' ||
    normalized === 'automate.healthexam.ai' ||
    normalized === 'custodian.healthexam.ai'
  );
}

export function resolveAyaDeployTierFromReturnUrl(
  raw: string | null | undefined
): AyaDeployTier | null {
  for (const host of collectHostnamesFromReturnUrl(raw)) {
    if (isProdHealthexamHost(host)) {
      return 'prod';
    }
    if (isQaHealthexamHost(host)) {
      return 'qa';
    }
  }
  return null;
}

/** Infer dev vs qa from the current auth app hostname (shared build serves both tiers). */
export function resolveAyaDeployTierFromHostname(): AyaDeployTier | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const host = window.location.hostname.toLowerCase();
  if (isProdHealthexamHost(host)) {
    return 'prod';
  }
  if (isQaHealthexamHost(host)) {
    return 'qa';
  }
  if (
    host.startsWith('dev-') &&
    (host.endsWith('.healthexam.ai') || host.endsWith('.healthexam.com'))
  ) {
    return 'dev';
  }
  return null;
}

export function resolveAyaDeployTier(
  returnUrl?: string | null | undefined
): AyaDeployTier {
  const fromReturn = resolveAyaDeployTierFromReturnUrl(returnUrl);
  if (fromReturn) {
    return fromReturn;
  }
  const fromHost = resolveAyaDeployTierFromHostname();
  if (fromHost) {
    return fromHost;
  }
  return 'dev';
}

export function ayaAppUrlForTier(tier: AyaDeployTier): string {
  if (tier === 'prod') {
    return PROD_AYA_APP_URL;
  }
  return tier === 'qa' ? QA_AYA_APP_URL : DEV_AYA_APP_URL;
}

export function wwwAppUrlForTier(tier: AyaDeployTier): string {
  if (tier === 'prod') {
    return PROD_WWW_APP_URL;
  }
  return tier === 'qa' ? QA_WWW_APP_URL : DEV_WWW_APP_URL;
}

export function healthpayAppUrlForTier(tier: AyaDeployTier): string {
  if (tier === 'prod') {
    return PROD_HEALTHPAY_APP_URL;
  }
  return tier === 'qa' ? QA_HEALTHPAY_APP_URL : DEV_HEALTHPAY_APP_URL;
}

export function threadsAppUrlForTier(tier: AyaDeployTier): string {
  if (tier === 'prod') {
    return PROD_THREADS_APP_URL;
  }
  return tier === 'qa' ? QA_THREADS_APP_URL : DEV_THREADS_APP_URL;
}

export function automateAppUrlForTier(tier: AyaDeployTier): string {
  if (tier === 'prod') {
    return PROD_AUTOMATE_APP_URL;
  }
  return tier === 'qa' ? QA_AUTOMATE_APP_URL : DEV_AUTOMATE_APP_URL;
}

export function custodianAppUrlForTier(tier: AyaDeployTier): string {
  if (tier === 'prod') {
    return PROD_CUSTODIAN_APP_URL;
  }
  return tier === 'qa' ? QA_CUSTODIAN_APP_URL : DEV_CUSTODIAN_APP_URL;
}

/** Resolve Health Pay app origin for the current auth tier (shared dev-auth + prod auth builds). */
export function resolveHealthpayAppUrl(
  returnUrl?: string | null | undefined
): string {
  return healthpayAppUrlForTier(resolveAyaDeployTier(returnUrl));
}

/** Resolve Threads app origin for the current auth tier (shared dev-auth + prod auth builds). */
export function resolveThreadsAppUrl(
  returnUrl?: string | null | undefined
): string {
  return threadsAppUrlForTier(resolveAyaDeployTier(returnUrl));
}

/** Resolve Aya Automate app origin for the current auth tier. */
export function resolveAutomateAppUrl(
  returnUrl?: string | null | undefined
): string {
  return automateAppUrlForTier(resolveAyaDeployTier(returnUrl));
}

/** Resolve Custodian app origin for the current auth tier. */
export function resolveCustodianAppUrl(
  returnUrl?: string | null | undefined
): string {
  return custodianAppUrlForTier(resolveAyaDeployTier(returnUrl));
}
